import { ReactNode, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components-new/button';
import { Input } from '@/components-new/input';

type DrugMarketDetailsFilterData = { ndc?: string; name?: string; product?: string };

type DrugMarketDetailsFiltersProps = {
  defaultValue: DrugMarketDetailsFilterData;
  onApplyFilters: (data: DrugMarketDetailsFilterData) => void;
  children?: ReactNode;
};

export const DrugMarketDetailsFilters = ({ defaultValue, onApplyFilters, children }: DrugMarketDetailsFiltersProps) => {
  const { control, handleSubmit, watch, reset } = useForm<DrugMarketDetailsFilterData>({
    defaultValues: defaultValue,
  });

  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const subscription = watch(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => formRef.current?.requestSubmit(), 300);
    });
    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [watch]);

  const onSubmit = handleSubmit((data) => onApplyFilters(data));

  const onReset = () => {
    reset({ ndc: '', name: '', product: '' });
    onApplyFilters({ ndc: '', name: '', product: '' });
  };

  return (
    <form ref={formRef} onSubmit={onSubmit} onReset={(e) => { e.preventDefault(); onReset(); }}>
      <div className="border-b border-zinc-200 bg-white">
        {children && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
            {children}
          </div>
        )}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-zinc-100">
          <div className="grid flex-1 auto-cols-fr grid-flow-col gap-2">
            <Controller
              control={control}
              name="ndc"
              render={({ field }) => (
                <Input {...field} type="search" placeholder="Search By NDC" maxLength={11} />
              )}
            />
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input {...field} type="search" placeholder="Search By Description" />
              )}
            />
            <Controller
              control={control}
              name="product"
              render={({ field }) => (
                <Input {...field} type="search" placeholder="Search By Product" />
              )}
            />
          </div>
          <Button outline type="reset" aria-label="Clear" className="shrink-0">Clear</Button>
        </div>
      </div>
    </form>
  );
};
